import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  WeeklySchedule,
  DayOfWeek,
  DaySchedule,
  DEFAULT_WEEKLY_SCHEDULE,
  SLOT_DURATION_MINUTES,
} from '../models/schedule.model';

const STORAGE_KEY = 'weekly_schedule';

/**
 * AvailabilityService — manages the professional's weekly schedule.
 * Persists to localStorage. Replace with Firestore/API when ready.
 */
@Injectable({ providedIn: 'root' })
export class AvailabilityService {
  private _schedule$ = new BehaviorSubject<WeeklySchedule>(this.load());

  schedule$: Observable<WeeklySchedule> = this._schedule$.asObservable();

  get scheduleSnapshot(): WeeklySchedule {
    return this._schedule$.value;
  }

  updateDay(day: DayOfWeek, changes: Partial<DaySchedule>): void {
    const updated = {
      ...this._schedule$.value,
      [day]: { ...this._schedule$.value[day], ...changes },
    };
    this._schedule$.next(updated);
    this.persist(updated);
  }

  save(schedule: WeeklySchedule): void {
    this._schedule$.next(schedule);
    this.persist(schedule);
  }

  /**
   * Generate time slots for a given date based on the weekly schedule.
   * Returns HH:mm strings in 30-min increments.
   */
  getSlotsForDate(date: string): string[] {
    const d = new Date(date + 'T00:00:00');
    const dow = d.getDay() as DayOfWeek;
    const day = this._schedule$.value[dow];
    if (!day.enabled) return [];
    return this.buildSlots(day.from, day.to);
  }

  private buildSlots(from: string, to: string): string[] {
    const [fh, fm] = from.split(':').map(Number);
    const [th, tm] = to.split(':').map(Number);
    const startMin = fh * 60 + fm;
    const endMin   = th * 60 + tm;
    const slots: string[] = [];
    for (let m = startMin; m < endMin; m += SLOT_DURATION_MINUTES) {
      const h = Math.floor(m / 60).toString().padStart(2, '0');
      const min = (m % 60).toString().padStart(2, '0');
      slots.push(`${h}:${min}`);
    }
    return slots;
  }

  private load(): WeeklySchedule {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : { ...DEFAULT_WEEKLY_SCHEDULE };
    } catch {
      return { ...DEFAULT_WEEKLY_SCHEDULE };
    }
  }

  private persist(schedule: WeeklySchedule): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(schedule));
    } catch {}
  }
}
